import lookups from '@/assets/json/clientLookups.json'
import router from '@/router'
import { useAuthStore } from '@/stores/auth'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import { DateTime } from 'luxon'
import { useEstablishmentDraftOverrideStore } from './useEstablishmentDraftOverrideStore'
import { useTypingCenterDraftOverrideStore } from './useTypingCenterDraftOverrideStore'

async function overrideAdminRegionId() {
  const authStore = useAuthStore()

  const { adminRegionId: administrativeRegionId } = authStore.individualInfo

  return {
    administrativeRegionId
  }
}

const use730DraftOverride = async () => {
  const ov = await overrideAdminRegionId()
  const { query } = router.currentRoute.value
  return {
    ...ov,
    withException: query.withException
  }
}

async function use873DraftOverride() {
  const globalStore = useGlobalStore()
  const formStore = useFormStore()
  const {
    serviceConfig: { mainServiceTransactionId }
  } = globalStore.startedService

  return {
    request: { ...formStore.form.values, mainServiceTransactionId }
  }
}

async function use663DraftOverride() {
  const formStore = useFormStore()
  let result = {}

  const actualArabicName = formStore.extraMap.arabicName
  const actualEnglishName = formStore.extraMap.englishName

  const arabicName = formStore.form.values.arabicApplicantName
  const englishName = formStore.form.values.englishApplicantName

  if (formStore.extraMap.englishName && formStore.extraMap.arabicName) {
    formStore.form.setValues({
      arabicName: actualArabicName,
      arabicApplicantName: actualArabicName,
      englishName: actualEnglishName,
      englishApplicantName: actualEnglishName
    })
    result = {
      arabicName: actualArabicName,
      arabicApplicantName: actualArabicName,
      englishName: actualEnglishName,
      englishApplicantName: actualEnglishName
    }
  } else {
    result = {
      arabicName,
      englishName
    }
  }

  return {
    request: { ...formStore.form.values, ...result }
  }
}

const use411DraftOverride = async () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap

  return {
    request: {
      ...formStore.form.values,
      ...formStore.draft.request
    },
    administrativeRegionId,
    withException,
    serviceTransactionId
  }
}

async function use719DraftOverride() {
  const formStore = useFormStore()
  return {
    request: {
      ...formStore.form.values,
      acknowledgement: formStore.form.values?.acknowledgement ? 1 : 0,
      localAddressUserActionEnum: formStore.extraMap.localAddressUserActionEnum
    },
    ...useAdminStrativeRegionIdAndWithExceptionOverride()
  }
}

const use919DraftOverride = () => {
  const formStore = useFormStore()
  const payload = {
    ...formStore.extraMap.res
  }
  return {
    request: {
      ...payload
    }
  }
}

const use573DraftOverride = () => {
  const formStore = useFormStore()
  return {
    ...formStore.form.values,
    ...formStore.draft
  }
}

const use575DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  return {
    request: {
      ...formStore.form.values,
      individualInfo: {}
    },
    administrativeRegionId,
    withException,
    serviceTransactionId
  }
}

const use1059DraftOverride = () => {
  const formStore = useFormStore()

  const { params, query } = router.currentRoute.value

  const { startingDate, EndingDate } = formStore.form.values

  const { months } = DateTime.fromFormat(EndingDate, 'dd/MM/yyyy')
    .diff(DateTime.fromFormat(startingDate, 'dd/MM/yyyy'), ['months'])
    .toObject()

  const payload = {
    startingDate,
    EndingDate,
    numberOfYears: Math.ceil(months),
    serviceTransId: params.serviceId,
    transactionReasonId: query.reasonId
  }
  return {
    request: {
      ...formStore.draft.request,
      ...formStore.form.values,
      ...payload
    },
    withException: false
  }
}

/**
 * since this is called on other steps other than request step,
 * the value might be overridden with *undefined*
 * so we need to check if the attribute exists in draft.request
 * and pass if it does
 */
const use770DraftOverride = () => {
  const formStore = useFormStore()

  let platformOperatingSystemsIds = formStore.form.values.platformOperatingSystemsIds?.join(',')

  const {
    draft: { request }
  } = formStore
  if ('platformOperatingSystemsIds' in request && request.platformOperatingSystemsIds) {
    platformOperatingSystemsIds = request.platformOperatingSystemsIds
  }

  return {
    request: {
      ...formStore.form.values,
      platformOperatingSystemsIds
    }
  }
}

const use490DraftOverride = () => {
  const formStore = useFormStore()
  const { withException, administrativeRegionId } = formStore.extraMap

  return {
    request: {
      ...formStore.form.values,
      individualInfo: {},
      administrativeRegionId
    },
    administrativeRegionId,
    withException
  }
}

const use445DraftOverride = () => {
  const formStore = useFormStore()
  const { fileServiceId, withException, administrativeRegionId } = formStore.extraMap
  const { AtTheRequestOfTheSponsor } = lookups
  return {
    request: {
      ...formStore.form.values,
      ...formStore.draft.request,
      individualInfo: {},
      addNewAddressFlag: false,
      transactionReasonId1: AtTheRequestOfTheSponsor,
      showWithoutSponsor: false,
      showWithSponsor: true,
      hasGreenOrGoldenVisaPendingRequest: false,
      isVirtualSponsor: false,
      isUsingSponsorIdentityCard: false
    },
    fileServiceId,
    administrativeRegionId,
    withException
  }
}

const useAdminStrativeRegionIdAndWithExceptionOverride = () => {
  const formStore = useFormStore()
  const globalStore = useGlobalStore()
  const { query } = router.currentRoute.value
  return {
    serviceTransactionId: globalStore.startedService.serviceTransactionId,
    administrativeRegionId: query.administrativeRegionId || 1,
    withException: query.withException || false,
    refDraftId: query.refDraftId
  }
}

const getdeliveryId = ({
  deliveryCompanyId,
  tawzeaDeliveryEmirateId,
  tawzeaDeliveryCityId,
  tawzeaDeliveryAreaId,
  emiratePostDeliveryEmirateId,
  emiratePostDeliveryCityId,
  emiratePostDeliveryAreaId,
  zajelDeliveryEmirateId,
  zajelDeliveryCityId,
  zajelDeliveryAreaId,
  amerDeliveryEmirateId,
  amerDeliveryCityId,
  amerDeliveryAreaId
}) => {
  const {
    deliveyCompany: { Tawzea, EmiratePost, Zajel, Amer }
  } = lookups
  if (deliveryCompanyId == Tawzea) {
    return {
      deliveryEmirateId: tawzeaDeliveryEmirateId,
      deliveryCityId: tawzeaDeliveryCityId,
      deliveryAreaId: tawzeaDeliveryAreaId
    }
  } else if (deliveryCompanyId == EmiratePost) {
    return {
      deliveryEmirateId: emiratePostDeliveryEmirateId,
      deliveryCityId: emiratePostDeliveryCityId,
      deliveryAreaId: emiratePostDeliveryAreaId
    }
  } else if (deliveryCompanyId == Zajel) {
    return {
      deliveryEmirateId: zajelDeliveryEmirateId,
      deliveryCityId: zajelDeliveryCityId,
      deliveryAreaId: zajelDeliveryAreaId
    }
  } else if (deliveryCompanyId == Amer) {
    return {
      deliveryEmirateId: amerDeliveryEmirateId,
      deliveryCityId: amerDeliveryCityId,
      deliveryAreaId: amerDeliveryAreaId
    }
  }
  return {
    deliveryEmirateId: '',
    deliveryCityId: '',
    deliveryAreaId: ''
  }
}
const useIcaidaFormOverride = () => {
  const formStore = useFormStore()
  const authStore = useAuthStore()
  const globalStore = useGlobalStore()
  const { serviceTransactionId } = globalStore.startedService
  const { withException, administrativeRegionId } = formStore.extraMap
  const {
    passportRelation: { holder },
    educationLevels: { none }
  } = lookups
  const {
    localAreaId,
    tawzeaDeliveryEmirateId,
    tawzeaDeliveryCityId,
    tawzeaDeliveryAreaId,
    emiratePostDeliveryEmirateId,
    emiratePostDeliveryCityId,
    emiratePostDeliveryAreaId,
    zajelDeliveryEmirateId,
    zajelDeliveryCityId,
    zajelDeliveryAreaId,
    amerDeliveryEmirateId,
    amerDeliveryCityId,
    amerDeliveryAreaId,
    deliveryCompanyId,
    applicantMobile = '',
    localMobile = '',
    identityNumber = '',
    applicantEmail = '',
    emailAddress = '',
    applicantIdentityNumber = '',
    arabicApplicantName = '',
    arabicName = '',
    sponsoredFirstNameArabic = '',
    sponsoredSecondNameArabic = '',
    sponsoredThirdNameArabic = '',
    sponsoredFourthNameArabic = '',
    sponsoredFamilyNameArabic = '',
    sponsoredFirstNameEnglish = '',
    sponsoredSecondNameEnglish = '',
    sponsoredThirdNameEnglish = '',
    sponsoredFourthNameEnglish = '',
    sponsoredFamilyNameEnglish = '',
    sponsoredPobox = ''
  } = formStore.form.values
  const { deliveryEmirateId, deliveryCityId, deliveryAreaId } = getdeliveryId({
    deliveryCompanyId,
    tawzeaDeliveryEmirateId,
    tawzeaDeliveryCityId,
    tawzeaDeliveryAreaId,
    emiratePostDeliveryEmirateId,
    emiratePostDeliveryCityId,
    emiratePostDeliveryAreaId,
    zajelDeliveryEmirateId,
    zajelDeliveryCityId,
    zajelDeliveryAreaId,
    amerDeliveryEmirateId,
    amerDeliveryCityId,
    amerDeliveryAreaId
  })
  let request580Date = {}
  if (serviceTransactionId == 580) {
    const _arabicName = [
      sponsoredFirstNameArabic,
      sponsoredSecondNameArabic,
      sponsoredThirdNameArabic,
      sponsoredFourthNameArabic,
      sponsoredFamilyNameArabic
    ]
      .join(' ')
      .trim()
    const _englishName = [
      sponsoredFirstNameEnglish,
      sponsoredSecondNameEnglish,
      sponsoredThirdNameEnglish,
      sponsoredFourthNameEnglish,
      sponsoredFamilyNameEnglish
    ]
      .join(' ')
      .trim()
    Object.assign(request580Date, {
      arabicName: _arabicName,
      englishName: _englishName,
      arabicApplicantName: _arabicName,
      englishApplicantName: _englishName,
      passportRelationId: holder,
      printingServicePointId: null,
      priorityId: 1,
      qualificationLevelId: none,
      userHadTheOldCardOrCopy: 0
    })
  }
  return {
    request: {
      ...formStore.draft.request,
      ...formStore.form.values,
      individualInfo: {},
      administrativeRegionId,
      residencyAddressAreaId: localAreaId,
      establishmentId: authStore.user.establishmentId || 0,
      deliveryEmirateId,
      deliveryCityId,
      deliveryAreaId,
      applicantMobile: applicantMobile || localMobile,
      localMobile: localMobile || applicantMobile,
      applicantIdentityNumber: applicantIdentityNumber || identityNumber,
      applicantEmail: applicantEmail || emailAddress,
      arabicApplicantName: arabicApplicantName || arabicName,
      addNewAddressFlag: false,
      deliveryLocalPobox: sponsoredPobox,
      DisableFastTrackOptions: false,
      ...request580Date
    },
    administrativeRegionId,
    withException
  }
}

const useIcaUnifiedFormOverride = () => {
  const formStore = useFormStore()
  const globalStore = useGlobalStore()
  const { serviceTransactionId } = globalStore.startedService
  const { withException, administrativeRegionId } = formStore.extraMap
  const {
    tawzeaDeliveryEmirateId = '',
    tawzeaDeliveryCityId = '',
    tawzeaDeliveryAreaId = '',
    emiratePostDeliveryEmirateId = '',
    emiratePostDeliveryCityId = '',
    emiratePostDeliveryAreaId = '',
    zajelDeliveryEmirateId = '',
    zajelDeliveryCityId = '',
    zajelDeliveryAreaId = '',
    amerDeliveryEmirateId = '',
    amerDeliveryCityId = '',
    amerDeliveryAreaId = '',
    deliveryCompanyId = '',
    numberOfYears = ''
  } = formStore.form.values
  const { deliveryEmirateId, deliveryCityId, deliveryAreaId } = getdeliveryId({
    deliveryCompanyId,
    tawzeaDeliveryEmirateId,
    tawzeaDeliveryCityId,
    tawzeaDeliveryAreaId,
    emiratePostDeliveryEmirateId,
    emiratePostDeliveryCityId,
    emiratePostDeliveryAreaId,
    zajelDeliveryEmirateId,
    zajelDeliveryCityId,
    zajelDeliveryAreaId,
    amerDeliveryEmirateId,
    amerDeliveryCityId,
    amerDeliveryAreaId
  })
  return {
    request: {
      ...formStore.form.values,
      individualInfo: {},
      deliveryAreaId,
      deliveryCityId,
      deliveryEmirateId,
      newIdCardDurationId: numberOfYears,
      priorityId: 1,
      uiActionId: 370,
      isParentInsideUae: 0
    },
    administrativeRegionId,
    withException,
    serviceTransactionId,
    applicantId: null,
    isHumanitarianCaseOptionSelected: true
  }
}

const use369DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const {
    myTransactionSelected = false,
    familyMembersSelected = false,
    collection = []
  } = formStore.form.values
  return {
    request: {
      ...formStore.form.values,
      sponsoredUnifiedNumber: 0,
      familyTransactions: familyMembersSelected ? 1 : 0,
      myTransactions: myTransactionSelected ? 1 : 0,
      selectedPerson: false,
      personUnifiedNumber: collection?.[0]?.unifiedNumber,
      collection: null
    },
    administrativeRegionId,
    withException,
    serviceTransactionId
  }
}
const use212DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const { sponsorDepartmentId = '', applicantId = '', isNewPassport = 0 } = formStore.form.values
  return {
    request: {
      ...formStore.form.values,
      isNewPassport,
      serviceTransactionId,
      violationCorrectionDepartmentId: sponsorDepartmentId
    },
    administrativeRegionId,
    withException,
    serviceTransactionId,
    applicantId
  }
}
const use206DraftOverride = () => {
  const formStore = useFormStore()
  const authStore = useAuthStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const {
    applicantId = '',
    fileNumber = '',
    serviceCode = '',
    departemntCode = ''
  } = formStore.form.values
  let extraData = {
    fileSeqNo: fileNumber
  }
  if (authStore.user.applicationId == 2) {
    extraData = {
      ...extraData,
      fileDepartmentCode: departemntCode,
      fileTypeCode: serviceCode
    }
  }
  return {
    request: {
      ...formStore.draft.request,
      ...formStore.form.values,
      ...extraData
    },
    administrativeRegionId,
    withException,
    serviceTransactionId,
    applicantId
  }
}
const use1040DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const { applicantId = '', fileNumber = '' } = formStore.form.values
  return {
    request: {
      ...formStore.form.values,
      fileSeqNo: fileNumber,
      individualInfo: {}
    },
    administrativeRegionId,
    withException,
    serviceTransactionId,
    applicantId
  }
}

const use92DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const { applicantId = '' } = formStore.form.values
  return {
    request: {
      ...formStore.form.values,
      individualInfo: {}
    },
    administrativeRegionId,
    withException,
    serviceTransactionId,
    applicantId
  }
}
const use903DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const { applicantArbFullName = '', applicantEngFullName = '' } = formStore.form.values
  return {
    request: {
      ...formStore.form.values,
      individualInfo: {},
      arabicName: applicantArbFullName,
      englishName: applicantEngFullName,
      withException
    },
    administrativeRegionId,
    serviceTransactionId
  }
}

const use739DraftOverride = () => {
  const formStore = useFormStore()
  const {
    violationStartDate,
    applicationDate,
    serviceTransactionId,
    withException,
    administrativeRegionId
  } = formStore.extraMap
  return {
    request: {
      ...formStore.form.values,
      individualInfo: {},
      applicationDate,
      violationEndDate: applicationDate,
      violationStartDate: DateTime.fromJSDate(new Date(violationStartDate))
        .set({
          hour: 6,
          minute: 0,
          second: 0,
          millisecond: 0
        })
        .toFormat("yyyy-MM-dd'T'HH:mm:ss")
    },
    withException,
    administrativeRegionId,
    serviceTransactionId
  }
}

const use446DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  return {
    request: {
      ...formStore.form.values,
      individualInfo: {}
    },
    withException,
    administrativeRegionId,
    serviceTransactionId
  }
}

const use412DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  return {
    request: {
      ...formStore.form.values,
      ...formStore.draft.request,
      individualInfo: {}
    },
    withException,
    administrativeRegionId,
    serviceTransactionId
  }
}
const use199DraftOverride = () => {
  const formStore = useFormStore()
  const globalStore = useGlobalStore()
  const { serviceTransactionId } = globalStore.startedService
  const { administrativeRegionId = '', withException = '' } = router.currentRoute.value.query
  return {
    request: {
      ...formStore.draft.request,
      ...formStore.form.values
    },
    withException: withException == 'true',
    administrativeRegionId,
    serviceTransactionId
  }
}

const use79DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  return {
    request: {
      ...formStore.form.values,
      individualInfo: {},
      sponsoredTypeId: 1
    },
    withException,
    // SOAR-1965. administrativeRegionId being null throws 500
    ...(administrativeRegionId && { administrativeRegionId }),
    serviceTransactionId
  }
}

const use187DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const { departmentCode = '' } = formStore.form.values
  return {
    request: {
      ...formStore.draft.request,
      ...formStore.form.values,
      individualInfo: {},
      identificater: !!departmentCode ? 2 : 1
    },
    withException,
    administrativeRegionId,
    serviceTransactionId
  }
}
const use477DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const {
    module: { residency }
  } = lookups
  return {
    request: {
      ...formStore.draft.request,
      ...formStore.form.values,
      serviceTransId: serviceTransactionId,
      fileModuleId: residency
    },
    withException,
    administrativeRegionId,
    serviceTransactionId
  }
}
const use184DraftOverride = () => {
  const authStore = useAuthStore()
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const {
    user: { applicationId }
  } = authStore
  const { localEmirateId = '', localCityId = '', detailedAddress = '' } = formStore.form.values
  let extraData = {}
  if (applicationId == 2) {
    extraData = { address: {} }
    extraData.address.name = `${localEmirateId} - ${localCityId} - ${detailedAddress}`
  }
  return {
    request: {
      ...formStore.form.values,
      ...extraData
    },
    withException,
    administrativeRegionId,
    serviceTransactionId
  }
}

const use192DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const { localEmirateId = '', localCityId = '', detailedAddress = '' } = formStore.form.values
  let extraData = { address: {} }
  extraData.address.name = `${localEmirateId} - ${localCityId} - ${detailedAddress}`
  return {
    request: {
      ...formStore.form.values,
      ...extraData
    },
    withException,
    administrativeRegionId,
    serviceTransactionId
  }
}

const use140DraftOverride = () => {
  const formStore = useFormStore()
  const authStore = useAuthStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const {
    establishmentRequestType: { ISSUE_NEW_TRADE_LICENSE }
  } = lookups
  const {
    user: { applicationId }
  } = authStore
  const {
    emirateId = '',
    cityId = '',
    detailedAddress = '',
    establishmentRequestActionTypeId = ''
  } = formStore.form.values
  let extraData = {}
  if (applicationId == 2) {
    extraData = { workAddress: {} }
    extraData.workAddress.name = `${emirateId} - ${cityId} - ${detailedAddress}`
    if (serviceTransactionId == 139) {
      extraData = {
        ...extraData,
        establishmentRequestActionTypeId: establishmentRequestActionTypeId
          ? establishmentRequestActionTypeId
          : ISSUE_NEW_TRADE_LICENSE,
        establishmentRequestTypeId: ISSUE_NEW_TRADE_LICENSE
      }
    }
  }
  return {
    request: {
      ...formStore.draft.request,
      ...formStore.form.values,
      individualInfo: {},
      ...extraData
    },
    withException,
    administrativeRegionId,
    serviceTransactionId
  }
}

const use401DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const { transactionReasonIds = '' } = formStore.form.values
  const {
    individualInfo: {
      genderId,
      unifiedNumber,
      identityNumber,
      nationalityId,
      englishFullName,
      arabicFullName,
      passportNumber
    }
  } = formStore.extraMap
  let extraObj = parentInfo({
    genderId,
    unifiedNumber,
    identityNumber,
    nationalityId,
    englishFullName,
    arabicFullName,
    passportNumber
  })
  return {
    request: {
      ...formStore.form.values,
      userHadTheOldCardOrCopy: 0,
      sponsorGenderId: genderId,
      ...extraObj,
      individualInfo: {},
      applicationRequestTypeId: 3,
      isParentInsideUae: 1,
      transactionReasonIds: Array.isArray(transactionReasonIds)
        ? transactionReasonIds.join(',')
        : transactionReasonIds
    },
    withException,
    administrativeRegionId,
    serviceTransactionId
  }
}
const parentInfo = ({
  genderId,
  unifiedNumber,
  identityNumber,
  nationalityId,
  englishFullName,
  arabicFullName,
  passportNumber
}) => {
  if (genderId == 2) {
    return {
      motherUnifiedNumber: unifiedNumber,
      motherEID: identityNumber,
      motherNationalityId: nationalityId,
      motherEnglishFullName: englishFullName,
      motherArabicFullName: arabicFullName,
      motherPassportNumber: passportNumber
    }
  } else {
    return {
      fatherUnifiedNumber: unifiedNumber,
      fatherEID: identityNumber,
      fatherNationalityId: nationalityId,
      fatherEnglishFullName: englishFullName,
      fatherArabicFullName: arabicFullName,
      fatherPassportNumber: passportNumber
    }
  }
}

const useCancelResidenceDraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const { applicantMobile = '' } = formStore.form.values
  return {
    request: {
      ...formStore.form.values,
      sponsorMobile: applicantMobile
    },
    withException,
    administrativeRegionId,
    serviceTransactionId
  }
}
const useResidenceDraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const { sponsorGenderId = '' } = formStore.form.values
  const {
    individualInfo: {
      unifiedNumber = '',
      identityNumber = '',
      nationalityId = '',
      englishFullName = '',
      arabicFullName = '',
      passportNumber = '',
      fileNumber = '',
      genderId = ''
    }
  } = formStore.extraMap
  const {
    ServiceTransactions: { cancelResidencyWithoutSponsored }
  } = lookups
  let extraObj = parentInfo({
    genderId: sponsorGenderId,
    unifiedNumber,
    identityNumber,
    nationalityId,
    englishFullName,
    arabicFullName,
    passportNumber
  })
  if (serviceTransactionId != cancelResidencyWithoutSponsored) {
    extraObj = {
      ...extraObj,
      sponsorName: arabicFullName,
      sponsorNumber: unifiedNumber
    }
  }

  return {
    request: {
      ...formStore.form.values,
      individualInfo: {},
      ...extraObj,
      sponsorNationalityId: nationalityId,
      sponsorFileNumber: fileNumber,
      sponsorGenderId: genderId
    },
    withException,
    administrativeRegionId,
    serviceTransactionId
  }
}

const setAdminRegionIdByIssuanceVisaEmirateId = () => {
  const formStore = useFormStore()
  const globalStore = useGlobalStore()

  const { query } = router.currentRoute.value

  const {
    applicantInformationId,
    requestNumber,
    applicantId,
    id,
    request: { hasPoliceClearanceCertificateAttachment }
  } = formStore.draft

  // for draft/update
  if (requestNumber) {
    return {
      request: {
        ...formStore.draft.request,
        ...formStore.form.values
      },
      applicantInformationId,
      applicantId,
      requestNumber,
      id,
      hasPoliceClearanceCertificateAttachment,
      administrativeRegionId: formStore.draft.request.issuanceVisaEmirateId,
      withException: query.withException || false,
      serviceTransactionId: globalStore.startedService.serviceTransactionId
    }
  }

  // for draft/add
  return {
    request: {
      ...formStore.draft.request,
      ...formStore.form.values
    },
    administrativeRegionId: formStore.form.values.issuanceVisaEmirateId,
    withException: query.withException || false,
    serviceTransactionId: globalStore.startedService.serviceTransactionId
  }
}

const useRefundDraftOverride = () => {
  const formStore = useFormStore()
  const { withException } = formStore.extraMap
  return {
    request: {
      ...formStore.form.values
    },
    withException
  }
}

const useGccVisaDraftOverride = () => {
  const formStore = useFormStore()
  const { withException } = formStore.extraMap
  return {
    request: {
      ...formStore.form.values,
      isDepartureLandPort: false
    },
    withException,
    requestApplyOption: 4,
    administrativeRegionId: 1
  }
}

const use417DraftOverride = () => {
  const formStore = useFormStore()
  const { arabicApplicantNameOriginal = '', englishApplicantNameOriginal = '' } =
    formStore.form.values
  const { administrativeRegionId } = formStore.extraMap
  return {
    request: {
      ...formStore.draft.request,
      ...formStore.form.values
    },
    administrativeRegionId,
    englishApplicantName: englishApplicantNameOriginal,
    arabicApplicantName: arabicApplicantNameOriginal
  }
}

const use1120DraftOverride = () => {
  const formStore = useFormStore()
  const globalStore = useGlobalStore()
  const { serviceTransactionId } = globalStore.startedService
  const { administrativeRegionId = '', withException = '' } = router.currentRoute.value.query
  const { complaintReasonesArray = [] } = formStore.form.values
  return {
    request: {
      ...formStore.form.values,
      ...formStore.draft.request,
      lookupModuleTransreasIds: complaintReasonesArray.join(',')
    },
    withException: withException == 'true',
    administrativeRegionId,
    serviceTransactionId
  }
}
const use905DraftOverride = () => {
  const formStore = useFormStore()
  const { serviceTransactionId, withException, administrativeRegionId } = formStore.extraMap
  const { complaintReasonesArray = [], identityNumber = '' } = formStore.form.values
  return {
    request: {
      ...formStore.form.values,
      ...formStore.draft.request,
      lookupModuleTransreasIds: complaintReasonesArray.join(','),
      identityNumber: identityNumber.replaceAll('-', '')
    },
    withException,
    administrativeRegionId,
    serviceTransactionId
  }
}

const useCommonDraftOverride = () => {
  const formStore = useFormStore()
  const globalStore = useGlobalStore()
  const { serviceTransactionId } = globalStore.startedService
  const { administrativeRegionId = '1', withException = '' } = router.currentRoute.value.query
  return {
    request: {
      ...formStore.draft.request,
      ...formStore.form.values
    },
    withException: withException == 'true',
    administrativeRegionId,
    serviceTransactionId
  }
}

const use632DraftOverride = () => {
  const formStore = useFormStore()
  const globalStore = useGlobalStore()
  const {
    serviceTransactionId,
    serviceConfig: { isLongTermVisaForMainPerson }
  } = globalStore.startedService
  const { administrativeRegionId = '', withException = '' } = router.currentRoute.value.query
  const {
    relationships: { samePerson }
  } = lookups

  return {
    request: {
      ...formStore.form.values,
      ...formStore.draft.request,
      ...(isLongTermVisaForMainPerson && { familyRelationshipId: samePerson })
    },
    withException: withException == 'true',
    administrativeRegionId,
    serviceTransactionId
  }
}

export default async function (serviceId) {
  const authStore = useAuthStore()

  const typingCenterOverrideStore = useTypingCenterDraftOverrideStore()
  const establishmentOverrideStore = useEstablishmentDraftOverrideStore()

  const establishmentOverrides = {
    162: establishmentOverrideStore.overridePayload162,
    393: () => establishmentOverrideStore.overridePayload(serviceId),
    607: () => establishmentOverrideStore.overridePayload(serviceId, { includeEstInfo: 'sponsor' }),
    870: () => establishmentOverrideStore.overrideIcaForm()
  }

  const goldenVisaOverrides = {
    603: setAdminRegionIdByIssuanceVisaEmirateId
  }

  const typingCenterOverrides = {
    24: typingCenterOverrideStore.overrideForm24,
    26: typingCenterOverrideStore.overrideForm24,
    27: typingCenterOverrideStore.overrideForm24,
    28: typingCenterOverrideStore.overrideForm24,
    36: typingCenterOverrideStore.overrideForm24,
    37: typingCenterOverrideStore.overrideForm24,
    38: typingCenterOverrideStore.overrideForm24,
    40: typingCenterOverrideStore.overrideForm24,
    43: typingCenterOverrideStore.overrideForm24,
    47: typingCenterOverrideStore.overrideForm24,
    48: typingCenterOverrideStore.overrideForm24,
    50: typingCenterOverrideStore.overrideForm24,
    51: typingCenterOverrideStore.overrideForm24,
    52: typingCenterOverrideStore.overrideForm24,
    53: typingCenterOverrideStore.overrideForm24,
    54: typingCenterOverrideStore.overrideForm24,
    57: typingCenterOverrideStore.overrideForm24,
    58: typingCenterOverrideStore.overrideForm24,
    59: typingCenterOverrideStore.overrideForm24,
    69: typingCenterOverrideStore.overrideForm24,
    89: typingCenterOverrideStore.overrideForm24,
    70: typingCenterOverrideStore.overrideForm24,
    71: typingCenterOverrideStore.overrideForm24,
    120: typingCenterOverrideStore.overrideForm24,
    121: typingCenterOverrideStore.overrideForm24,
    383: typingCenterOverrideStore.overrideForm24,
    385: typingCenterOverrideStore.overrideForm24,
    386: typingCenterOverrideStore.overrideForm24,
    387: typingCenterOverrideStore.overrideForm24,
    400: typingCenterOverrideStore.overrideForm24,
    413: typingCenterOverrideStore.overrideForm24,
    414: typingCenterOverrideStore.overrideForm24,
    420: typingCenterOverrideStore.overrideForm24,
    421: typingCenterOverrideStore.overrideForm24,
    423: typingCenterOverrideStore.overrideForm24,
    429: typingCenterOverrideStore.overrideForm24,
    436: typingCenterOverrideStore.overrideForm24,
    437: typingCenterOverrideStore.overrideForm24,
    438: typingCenterOverrideStore.overrideForm24,
    490: typingCenterOverrideStore.overrideForm24,
    515: typingCenterOverrideStore.overrideForm24,
    567: typingCenterOverrideStore.overrideForm24,
    569: typingCenterOverrideStore.overrideForm24,
    599: typingCenterOverrideStore.overrideForm24,
    600: typingCenterOverrideStore.overrideForm24,
    601: typingCenterOverrideStore.overrideForm24,
    602: typingCenterOverrideStore.overrideForm24,
    613: typingCenterOverrideStore.overrideForm24,
    632: typingCenterOverrideStore.overrideForm24,
    633: typingCenterOverrideStore.overrideForm24,
    635: typingCenterOverrideStore.overrideForm24,
    636: typingCenterOverrideStore.overrideForm24,
    639: typingCenterOverrideStore.overrideForm24,
    640: typingCenterOverrideStore.overrideForm24,
    641: typingCenterOverrideStore.overrideForm24,
    642: typingCenterOverrideStore.overrideForm24,
    644: typingCenterOverrideStore.overrideForm24,
    645: typingCenterOverrideStore.overrideForm24,
    646: typingCenterOverrideStore.overrideForm24,
    647: typingCenterOverrideStore.overrideForm24,
    648: typingCenterOverrideStore.overrideForm24,
    664: typingCenterOverrideStore.overrideForm24,
    656: typingCenterOverrideStore.overrideForm24,
    657: typingCenterOverrideStore.overrideForm24,
    658: typingCenterOverrideStore.overrideForm24,
    659: typingCenterOverrideStore.overrideForm24,
    662: typingCenterOverrideStore.overrideForm24,
    665: typingCenterOverrideStore.overrideForm24,
    666: typingCenterOverrideStore.overrideForm24,
    667: typingCenterOverrideStore.overrideForm24,
    668: typingCenterOverrideStore.overrideForm24,
    676: typingCenterOverrideStore.overrideForm24,
    677: typingCenterOverrideStore.overrideForm24,
    678: typingCenterOverrideStore.overrideForm24,
    679: typingCenterOverrideStore.overrideForm24,
    680: typingCenterOverrideStore.overrideForm24,
    681: typingCenterOverrideStore.overrideForm24,
    685: typingCenterOverrideStore.overrideForm24,
    686: typingCenterOverrideStore.overrideForm24,
    687: typingCenterOverrideStore.overrideForm24,
    688: typingCenterOverrideStore.overrideForm24,
    689: typingCenterOverrideStore.overrideForm24,
    690: typingCenterOverrideStore.overrideForm24,
    753: typingCenterOverrideStore.overrideForm24,
    755: typingCenterOverrideStore.overrideForm24,
    756: typingCenterOverrideStore.overrideForm24,
    757: typingCenterOverrideStore.overrideForm24,
    758: typingCenterOverrideStore.overrideForm24,
    759: typingCenterOverrideStore.overrideForm24,
    763: typingCenterOverrideStore.overrideForm24,
    764: typingCenterOverrideStore.overrideForm24,
    765: typingCenterOverrideStore.overrideForm24,
    768: typingCenterOverrideStore.overrideForm24,
    771: typingCenterOverrideStore.overrideForm24,
    773: typingCenterOverrideStore.overrideForm24,
    774: typingCenterOverrideStore.overrideForm24,
    775: typingCenterOverrideStore.overrideForm24,
    776: typingCenterOverrideStore.overrideForm24,
    754: typingCenterOverrideStore.overrideForm24,
    760: typingCenterOverrideStore.overrideForm24,
    769: typingCenterOverrideStore.overrideForm24,
    777: typingCenterOverrideStore.overrideForm24,
    778: typingCenterOverrideStore.overrideForm24,
    785: typingCenterOverrideStore.overrideForm24,
    820: typingCenterOverrideStore.overrideForm24,
    916: typingCenterOverrideStore.overrideForm24,
    926: typingCenterOverrideStore.overrideForm24,
    928: typingCenterOverrideStore.overrideForm24,
    931: typingCenterOverrideStore.overrideForm24,
    933: typingCenterOverrideStore.overrideForm24,
    935: typingCenterOverrideStore.overrideForm24,
    938: typingCenterOverrideStore.overrideForm24,
    943: typingCenterOverrideStore.overrideForm24,
    948: typingCenterOverrideStore.overrideForm24,
    950: typingCenterOverrideStore.overrideForm24,
    951: typingCenterOverrideStore.overrideForm24,
    952: typingCenterOverrideStore.overrideForm24,
    963: typingCenterOverrideStore.overrideForm24,
    964: typingCenterOverrideStore.overrideForm24,
    955: typingCenterOverrideStore.overrideForm24,
    959: typingCenterOverrideStore.overrideForm24,
    965: typingCenterOverrideStore.overrideForm24,
    973: typingCenterOverrideStore.overrideForm24,
    976: typingCenterOverrideStore.overrideForm24,
    977: typingCenterOverrideStore.overrideForm24,
    978: typingCenterOverrideStore.overrideForm24,
    979: typingCenterOverrideStore.overrideForm24,
    980: typingCenterOverrideStore.overrideForm24,
    981: typingCenterOverrideStore.overrideForm24,
    982: typingCenterOverrideStore.overrideForm24,
    983: typingCenterOverrideStore.overrideForm24,
    985: typingCenterOverrideStore.overrideForm24,
    987: typingCenterOverrideStore.overrideForm24,
    989: typingCenterOverrideStore.overrideForm24,
    991: typingCenterOverrideStore.overrideForm24,
    993: typingCenterOverrideStore.overrideForm24,
    994: typingCenterOverrideStore.overrideForm24,
    995: typingCenterOverrideStore.overrideForm24,
    1005: typingCenterOverrideStore.overrideForm24,
    1006: typingCenterOverrideStore.overrideForm24,
    1007: typingCenterOverrideStore.overrideForm24,
    1008: typingCenterOverrideStore.overrideForm24,
    1009: typingCenterOverrideStore.overrideForm24,
    1010: typingCenterOverrideStore.overrideForm24,
    1018: typingCenterOverrideStore.overrideForm24,
    1023: typingCenterOverrideStore.overrideForm24,
    1026: typingCenterOverrideStore.overrideForm24,
    1031: typingCenterOverrideStore.overrideForm24,
    1033: typingCenterOverrideStore.overrideForm24,
    1035: typingCenterOverrideStore.overrideForm24,
    1038: typingCenterOverrideStore.overrideForm24,
    1041: typingCenterOverrideStore.overrideForm24,
    1042: typingCenterOverrideStore.overrideForm24,
    1044: typingCenterOverrideStore.overrideForm24,
    1055: typingCenterOverrideStore.overrideForm24,
    1062: typingCenterOverrideStore.overrideForm24,
    1092: typingCenterOverrideStore.overrideForm24,
    1093: typingCenterOverrideStore.overrideForm24,
    1095: typingCenterOverrideStore.overrideForm24,
    1097: typingCenterOverrideStore.overrideForm24,
    1099: typingCenterOverrideStore.overrideForm24,
    1100: typingCenterOverrideStore.overrideForm24,
    1102: typingCenterOverrideStore.overrideForm24,
    1104: typingCenterOverrideStore.overrideForm24,
    1105: typingCenterOverrideStore.overrideForm24,
    1106: typingCenterOverrideStore.overrideForm24,
    1108: typingCenterOverrideStore.overrideForm24,
    1110: typingCenterOverrideStore.overrideForm24,
    1112: typingCenterOverrideStore.overrideForm24,
    1113: typingCenterOverrideStore.overrideForm24,
    1116: typingCenterOverrideStore.overrideForm24,
    1118: typingCenterOverrideStore.overrideForm24,
    1123: typingCenterOverrideStore.overrideForm24,
    1125: typingCenterOverrideStore.overrideForm24,
    1127: typingCenterOverrideStore.overrideForm24,

    78: use79DraftOverride,
    424: use79DraftOverride,
    1057: use79DraftOverride,

    406: useCancelResidenceDraftOverride,
    443: useCancelResidenceDraftOverride,
    445: useCancelResidenceDraftOverride
  }

  const mapping = {
    967: useCommonDraftOverride,
    944: useCommonDraftOverride,
    432: useCommonDraftOverride,
    939: useCommonDraftOverride,
    941: useCommonDraftOverride,
    942: useCommonDraftOverride,
    945: useCommonDraftOverride,
    946: useCommonDraftOverride,
    873: use873DraftOverride,
    583: overrideAdminRegionId,
    587: useAdminStrativeRegionIdAndWithExceptionOverride,
    582: overrideAdminRegionId,
    730: use730DraftOverride,
    731: overrideAdminRegionId,
    732: overrideAdminRegionId,
    733: overrideAdminRegionId,
    734: overrideAdminRegionId,
    37: useAdminStrativeRegionIdAndWithExceptionOverride,
    995: useAdminStrativeRegionIdAndWithExceptionOverride,
    632: use632DraftOverride,
    633: use632DraftOverride,
    635: useAdminStrativeRegionIdAndWithExceptionOverride,
    636: useAdminStrativeRegionIdAndWithExceptionOverride,
    753: useAdminStrativeRegionIdAndWithExceptionOverride,
    639: useAdminStrativeRegionIdAndWithExceptionOverride,
    928: useAdminStrativeRegionIdAndWithExceptionOverride,
    933: useAdminStrativeRegionIdAndWithExceptionOverride,
    950: useAdminStrativeRegionIdAndWithExceptionOverride,
    951: useAdminStrativeRegionIdAndWithExceptionOverride,
    952: useAdminStrativeRegionIdAndWithExceptionOverride,
    1005: useAdminStrativeRegionIdAndWithExceptionOverride,
    1006: useAdminStrativeRegionIdAndWithExceptionOverride,
    1007: useAdminStrativeRegionIdAndWithExceptionOverride,
    1008: useAdminStrativeRegionIdAndWithExceptionOverride,
    1009: useAdminStrativeRegionIdAndWithExceptionOverride,
    1010: useAdminStrativeRegionIdAndWithExceptionOverride,
    1104: useAdminStrativeRegionIdAndWithExceptionOverride,
    1105: useAdminStrativeRegionIdAndWithExceptionOverride,
    561: useAdminStrativeRegionIdAndWithExceptionOverride,
    766: useAdminStrativeRegionIdAndWithExceptionOverride,
    762: useAdminStrativeRegionIdAndWithExceptionOverride,
    926: useAdminStrativeRegionIdAndWithExceptionOverride,
    458: useAdminStrativeRegionIdAndWithExceptionOverride,
    713: useAdminStrativeRegionIdAndWithExceptionOverride,
    714: useAdminStrativeRegionIdAndWithExceptionOverride,
    784: useAdminStrativeRegionIdAndWithExceptionOverride,
    593: useAdminStrativeRegionIdAndWithExceptionOverride,
    663: use663DraftOverride,
    411: use411DraftOverride,
    564: use411DraftOverride,
    565: use411DraftOverride,
    719: use719DraftOverride,
    434: use411DraftOverride,
    435: use411DraftOverride,
    919: use919DraftOverride,
    573: use573DraftOverride,
    574: use573DraftOverride,
    575: use575DraftOverride,
    1059: use1059DraftOverride,
    770: use770DraftOverride,
    490: use490DraftOverride,
    445: use445DraftOverride,
    571: useIcaidaFormOverride,
    572: useIcaidaFormOverride,
    580: useIcaidaFormOverride,
    502: useIcaUnifiedFormOverride,
    503: useIcaUnifiedFormOverride,
    556: useIcaUnifiedFormOverride,
    900: useIcaUnifiedFormOverride,
    369: use369DraftOverride,
    212: use212DraftOverride,
    206: use206DraftOverride,
    207: use206DraftOverride,
    208: use206DraftOverride,
    209: use206DraftOverride,
    1040: use1040DraftOverride,
    92: use92DraftOverride,
    903: use903DraftOverride,
    767: useCommonDraftOverride,
    739: use739DraftOverride,
    446: use446DraftOverride,
    447: use446DraftOverride,
    412: use412DraftOverride,
    79: use79DraftOverride,
    1057: use79DraftOverride,
    187: use187DraftOverride,
    401: use401DraftOverride,
    70: useResidenceDraftOverride,
    71: useResidenceDraftOverride,
    121: useResidenceDraftOverride,
    122: useResidenceDraftOverride,
    406: useResidenceDraftOverride,
    443: useResidenceDraftOverride,
    375: useRefundDraftOverride,
    376: useRefundDraftOverride,
    41: useGccVisaDraftOverride,
    42: useGccVisaDraftOverride,
    377: useGccVisaDraftOverride,
    139: use140DraftOverride,
    140: use140DraftOverride,
    141: use140DraftOverride,
    184: use184DraftOverride,
    185: use184DraftOverride,
    200: use184DraftOverride,
    201: use184DraftOverride,
    202: use184DraftOverride,
    203: use184DraftOverride,
    409: use184DraftOverride,
    410: use184DraftOverride,
    192: use192DraftOverride,
    193: use192DraftOverride,
    477: use477DraftOverride,
    212: use477DraftOverride,
    418: use477DraftOverride,
    199: use199DraftOverride,
    417: use417DraftOverride,
    430: useCommonDraftOverride,
    433: useCommonDraftOverride,
    495: useCommonDraftOverride,
    682: useCommonDraftOverride,
    683: useCommonDraftOverride,
    1090: useCommonDraftOverride,
    905: use905DraftOverride,
    1028: use905DraftOverride,
    1029: use905DraftOverride,
    1030: use905DraftOverride,
    1120: use1120DraftOverride,
    ...establishmentOverrides,
    ...goldenVisaOverrides,
    ...(authStore.isUserTypingCenter && typingCenterOverrides)
  }

  if (Object.keys(mapping).some((m) => m == serviceId)) {
    return await mapping[serviceId]()
  }

  console.info(`There's no draft override mapped for ${serviceId}`)
  return Promise.resolve({})
}
